import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { useLang } from "@/lib/i18n";

/**
 * Renders the body-fragment content of a static legal HTML file.
 * The backend serves these from <backend-dir>/legal/{name}.html at GET /api/legal/{name}
 * — files are read on every request, so edits require no rebuild/redeploy.
 */
export default function LegalPage({ pageKey, fileName }) {
  const { t } = useLang();
  const [html, setHtml] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const url = `/api/legal/${fileName}`;
    fetch(url, { cache: "no-store" })
      .then(async (r) => {
        if (!r.ok) {
          let msg = `HTTP ${r.status}`;
          try {
            const body = await r.text();
            if (body) msg += ` — ${body.slice(0, 200)}`;
          } catch (e) { /* ignore */ }
          throw new Error(msg);
        }
        return r.text();
      })
      .then((text) => {
        if (!cancelled) setHtml(text);
      })
      .catch((e) => {
        if (!cancelled) setErrorDetail(e.message || "load failed");
      });
    return () => {
      cancelled = true;
    };
  }, [fileName]);

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12" data-testid={`legal-page-${pageKey}`}>
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-neutral-900 dark:hover:text-white mb-8"
        data-testid="legal-back-link"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("brand")}
      </Link>

      <h1
        className="text-3xl md:text-5xl font-black tracking-tighter text-neutral-900 dark:text-white mb-8"
        data-testid={`legal-heading-${pageKey}`}
      >
        {t(pageKey)}
      </h1>

      {errorDetail && (
        <div
          className="p-6 border border-[#ff3b30]/40 bg-[#ff3b30]/5"
          data-testid="legal-error"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#ff3b30] shrink-0 mt-0.5" />
            <div className="text-sm space-y-2">
              <div className="text-[#ff3b30] font-mono uppercase tracking-widest text-xs">
                {t("legalError")}
              </div>
              <p className="text-neutral-700 dark:text-neutral-300">
                {t("legalErrorHint")}{" "}
                <code className="font-mono text-xs bg-black/5 dark:bg-white/10 px-1 py-0.5">
                  backend/legal/{fileName}.html
                </code>
              </p>
              <p className="text-neutral-500 font-mono text-[11px]">
                {errorDetail}
              </p>
            </div>
          </div>
        </div>
      )}

      {!errorDetail && html === null && (
        <p
          className="font-mono text-xs uppercase tracking-widest text-neutral-500 animate-pulse"
          data-testid="legal-loading"
        >
          {t("legalLoading")}
        </p>
      )}

      {!errorDetail && html !== null && (
        <article
          data-testid={`legal-content-${pageKey}`}
          className="legal-prose text-neutral-800 dark:text-neutral-200 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  );
}
